import { useRef, useEffect, useState } from 'react';
import Lottie from 'lottie-react';
import { Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TrailerModal } from './TrailerModal';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import homeHeroAnimation from '@/assets/home-hero.json';

interface HeadlineWord {
  text: string;
  color: string;
}

interface PageHeroSectionProps {
  headline: HeadlineWord[];
  paragraphText: string;
  paragraphBoldText?: string;
  lottieData?: object;
  showTrailerButton?: boolean;
  paragraphMaxWidth?: string;
}

export function PageHeroSection({ headline, paragraphText, paragraphBoldText, lottieData, showTrailerButton = true, paragraphMaxWidth = '250px' }: PageHeroSectionProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [headlineComplete, setHeadlineComplete] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [trailerModalOpen, setTrailerModalOpen] = useState(false);
  const [trailerId, setTrailerId] = useState<string | null>(null);
  const [trailerButtonReady, setTrailerButtonReady] = useState(false);

  // Fetch trailer ID from site settings
  useEffect(() => {
    const fetchTrailerId = async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'intro_video_vimeo_id')
        .maybeSingle();
      
      if (!error && data?.value) {
        setTrailerId(data.value);
      }
    };
    fetchTrailerId();
  }, []);

  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    setPrefersReducedMotion(reducedMotion);
    
    if (reducedMotion) {
      setIsVisible(true);
      setHeadlineComplete(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.2) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: [0.2, 0.3] }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const allLetters = headline.flatMap(word => 
    word.text.split('').map(letter => ({ letter, color: word.color }))
  );

  const totalLetters = allLetters.length;
  const letterDelay = 40;
  const headlineDuration = totalLetters * letterDelay;

  useEffect(() => {
    if (isVisible && !prefersReducedMotion) {
      const timer = setTimeout(() => {
        setHeadlineComplete(true);
      }, headlineDuration + 600);
      return () => clearTimeout(timer);
    }
  }, [isVisible, prefersReducedMotion, headlineDuration]);

  // Trigger trailer button fade-in only after both headline is done and trailerId is loaded
  useEffect(() => {
    if (headlineComplete && trailerId && showTrailerButton) {
      // Small delay to ensure the element is in DOM before triggering transition
      const timer = setTimeout(() => setTrailerButtonReady(true), 50);
      return () => clearTimeout(timer);
    }
  }, [headlineComplete, trailerId, showTrailerButton]);

  const handleWatchTrailer = () => {
    if (trailerId) {
      setTrailerModalOpen(true);
    } else {
      toast.info("Video coming soon! Check back later.");
    }
  };

  return (
    <>
      <section 
        ref={sectionRef}
        className="relative w-full overflow-hidden mt-[25px] md:mt-[20px] lg:mt-[60px]"
      >
        {/* Desktop Layout */}
        <div className="hidden lg:block relative w-full max-w-[1440px] mx-auto">
          {/* Lottie Animation - Right Side */}
          <div 
            className="absolute right-0 top-0 w-[55%] overflow-hidden z-0"
            style={{
              opacity: prefersReducedMotion ? 1 : isVisible ? 1 : 0,
              transition: 'opacity 800ms ease-out',
            }}
          >
            <Lottie
              animationData={lottieData || homeHeroAnimation}
              loop={false}
              autoplay={true}
              className="w-full h-auto"
              rendererSettings={{
                preserveAspectRatio: 'xMidYMid meet',
              }}
            />
          </div>

          {/* Text Content - Left Side, Overlapping */}
          <div 
            className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8"
          >
            <div 
              className="flex flex-col"
            >
              {/* Headline */}
              <h1 className="text-hero whitespace-pre-line mb-8">
                {allLetters.map((item, index) => {
                  const delay = index * letterDelay + 400;
                  const shouldAnimate = isVisible && !prefersReducedMotion;
                  
                  if (item.letter === '\n') {
                    return <br key={index} />;
                  }
                  
                  return (
                    <span
                      key={index}
                      style={{
                        color: item.color,
                        display: 'inline-block',
                        opacity: prefersReducedMotion ? 1 : shouldAnimate ? 1 : 0,
                        transform: prefersReducedMotion 
                          ? 'translateY(0)' 
                          : shouldAnimate 
                            ? 'translateY(0)' 
                            : 'translateY(20px)',
                        transition: `opacity 400ms ease-out ${delay}ms, transform 400ms ease-out ${delay}ms`,
                        minWidth: item.letter === ' ' ? '0.3em' : undefined,
                      }}
                    >
                      {item.letter === ' ' ? '\u00A0' : item.letter}
                    </span>
                  );
                })}
              </h1>

              {/* Paragraph */}
              <p 
                className={`text-paragraph-1 text-foreground mb-10 pl-[10px]`}
                style={{
                  maxWidth: paragraphMaxWidth,
                  fontSize: 'clamp(14px, 1vw, 18px)',
                  opacity: prefersReducedMotion ? 1 : headlineComplete ? 1 : 0,
                  transform: prefersReducedMotion 
                    ? 'translateY(0)' 
                    : headlineComplete 
                      ? 'translateY(0)' 
                      : 'translateY(20px)',
                  transition: 'opacity 600ms ease-out, transform 600ms ease-out',
                }}
              >
                {paragraphText}
                {paragraphBoldText && (
                  <>
                    <br />
                    <span className="font-bold">{paragraphBoldText}</span>
                  </>
                )}
              </p>

              {/* Watch Trailer Button - always reserve space to prevent layout jump */}
              {showTrailerButton && (
                <div
                  style={{
                    opacity: prefersReducedMotion ? (trailerId ? 1 : 0) : trailerButtonReady ? 1 : 0,
                    transform: prefersReducedMotion 
                      ? 'translateY(0)' 
                      : trailerButtonReady 
                        ? 'translateY(0)' 
                        : 'translateY(20px)',
                    transition: 'opacity 600ms ease-out, transform 600ms ease-out',
                    visibility: trailerId ? 'visible' : 'hidden',
                    minHeight: '48px',
                  }}
                >
                  <Button
                    variant="outline"
                    size="cta"
                    className="text-button text-foreground border-foreground hover:bg-foreground/10 transition-all duration-300"
                    onClick={handleWatchTrailer}
                  >
                    WATCH TRAILER
                    <Play className="w-4 h-4 ml-2 fill-current" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile & Tablet Layout */}
        <div className="lg:hidden w-full">
          {/* Lottie Animation - Top */}
          <div 
            className="relative w-full h-[300px] md:h-[400px] overflow-hidden"
            style={{
              opacity: prefersReducedMotion ? 1 : isVisible ? 1 : 0,
              transition: 'opacity 800ms ease-out',
            }}
          >
            <Lottie
              animationData={lottieData || homeHeroAnimation}
              loop={false}
              autoplay={true}
              className="w-full h-full"
              style={{
                width: '100%',
                height: '100%',
              }}
              rendererSettings={{
                preserveAspectRatio: 'xMidYMid slice',
              }}
            />
            {/* Gradient fade overlay at bottom */}
            <div 
              className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none"
              style={{
                background: 'linear-gradient(to bottom, transparent, hsl(var(--background)))'
              }}
            />
          </div>

          {/* Text Content */}
          <div className="w-full flex flex-col items-center md:items-start px-4 md:px-8 py-4 md:py-6">
            <h1 className="text-hero mb-4 md:mb-6 text-center md:text-left">
              {allLetters.map((item, index) => {
                const delay = index * letterDelay + 400;
                const shouldAnimate = isVisible && !prefersReducedMotion;
                
                if (item.letter === '\n') {
                  // Show line break on desktop, space on tablet/mobile
                  return (
                    <span key={index}>
                      <br className="hidden lg:inline" />
                      <span className="lg:hidden"> </span>
                    </span>
                  );
                }
                
                return (
                  <span
                    key={index}
                    style={{
                      color: item.color,
                      display: 'inline',
                      opacity: prefersReducedMotion ? 1 : shouldAnimate ? 1 : 0,
                      transform: prefersReducedMotion 
                        ? 'translateY(0)' 
                        : shouldAnimate 
                          ? 'translateY(0)' 
                          : 'translateY(20px)',
                      transition: `opacity 400ms ease-out ${delay}ms, transform 400ms ease-out ${delay}ms`,
                      minWidth: item.letter === ' ' ? '0.3em' : undefined,
                    }}
                  >
                    {item.letter === ' ' ? '\u00A0' : item.letter}
                  </span>
                );
              })}
            </h1>
            <p 
              className="text-paragraph-1 text-foreground max-w-xs md:max-w-sm mb-6 md:mb-8 text-center md:text-left"
              style={{
                opacity: prefersReducedMotion ? 1 : headlineComplete ? 1 : 0,
                transform: prefersReducedMotion 
                  ? 'translateY(0)' 
                  : headlineComplete 
                    ? 'translateY(0)' 
                    : 'translateY(20px)',
                transition: 'opacity 600ms ease-out, transform 600ms ease-out',
              }}
            >
              {paragraphText}
              {paragraphBoldText && (
                <>
                  <br />
                  <span className="font-bold">{paragraphBoldText}</span>
                </>
              )}
            </p>

            {/* Watch Trailer Button - always reserve space to prevent layout jump */}
            {showTrailerButton && (
              <div
                style={{
                  opacity: prefersReducedMotion ? (trailerId ? 1 : 0) : trailerButtonReady ? 1 : 0,
                  transform: prefersReducedMotion 
                    ? 'translateY(0)' 
                    : trailerButtonReady 
                      ? 'translateY(0)' 
                      : 'translateY(20px)',
                  transition: 'opacity 600ms ease-out, transform 600ms ease-out',
                  visibility: trailerId ? 'visible' : 'hidden',
                  minHeight: '48px',
                }}
              >
                <Button
                  variant="outline"
                  size="cta"
                  className="text-button text-foreground border-foreground hover:bg-foreground/10 transition-all duration-300"
                  onClick={handleWatchTrailer}
                >
                  WATCH TRAILER
                  <Play className="w-4 h-4 ml-2 fill-current" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Trailer Modal */}
      <TrailerModal
        isOpen={trailerModalOpen}
        onClose={() => setTrailerModalOpen(false)}
        vimeoId={trailerId}
        title="Tenacity Music Trailer"
      />
    </>
  );
}
